import { clsx } from 'clsx';
import { LockIcon, LockOpenIcon, PauseIcon, PlayIcon, Trash2Icon } from 'lucide-react';
import { bpToPercent } from '@redirector/shared';
import { IconButton } from '../components/IconButton.js';
import { Input } from '../components/ui/input.js';
import { Slider } from '../components/ui/slider.js';
import { formatPercent } from '../lib/format.js';
import { seriesColor } from '../lib/series.js';
import type { DraftRow } from './useWeightDraft.js';

export type WeightRowProps = {
  row: DraftRow;
  index: number;
  /** Share of live traffic: 0 for a paused row, whatever the weight says. */
  effectivePct: number;
  canEdit: boolean;
  onWeight: (percent: number) => void;
  onUrl: (url: string) => void;
  onToggleLock: () => void;
  onToggleEnabled: () => void;
  onRemove: () => void;
};

/** The slider is a coarse pointer affordance; the number field is the precise one. */
/* Zero, not one. The floor was 1% back when a destination at 0% could not be
   saved, so the slider refused to express a state the editor refused to accept.
   0% is now a parked destination, which makes dragging a slider to the bottom
   the most obvious way to park one — and stopping at 1% left the operator
   fighting the control to reach a value the form would have taken. */
const SLIDER_MIN = 0;
const SLIDER_MAX = 100;
const SLIDER_STEP = 0.5;

export function WeightRow(props: WeightRowProps) {
  const { row, canEdit } = props;
  const configured = bpToPercent(row.weightBp);

  return (
    <div
      className={clsx(
        'grid items-center gap-(--space-3) border-b border-rule py-(--space-3)',
        'grid-cols-[10px_minmax(0,1fr)_7rem_9rem_auto]',
        !row.enabled && 'opacity-60',
      )}
    >
      <span
        aria-hidden="true"
        className="h-[10px] w-[10px] rounded-full"
        style={{ background: seriesColor(props.index) }}
      />

      <div className="min-w-0">
        <label className="sr-only" htmlFor={`${row.key}-url`}>
          Destination URL
        </label>
        <Input
          id={`${row.key}-url`}
          className={clsx(
            'px-(--space-2) py-(--space-1) font-mono text-[length:var(--text-small)]',
            row.urlError !== undefined && 'border-danger',
          )}
          spellCheck={false}
          disabled={!canEdit}
          aria-invalid={row.urlError !== undefined}
          {...(row.urlError === undefined ? {} : { 'aria-describedby': `${row.key}-url-error` })}
          value={row.url}
          onChange={(event) => props.onUrl(event.target.value)}
        />
        {row.urlError !== undefined && (
          <p
            id={`${row.key}-url-error`}
            role="alert"
            className="mt-(--space-1) text-[length:var(--text-small)] text-danger"
          >
            {row.urlError}
          </p>
        )}
      </div>

      <div className="flex items-baseline gap-(--space-1)">
        <label className="sr-only" htmlFor={`${row.key}-weight`}>
          Weight percentage
        </label>
        <Input
          id={`${row.key}-weight`}
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={0.01}
          disabled={!canEdit}
          className={clsx(
            'h-(--control-height-sm) w-[4.5rem] px-(--space-2) text-right text-[length:var(--text-small)]',
            /* Still marked, no longer an error: a row at zero saves fine now,
               but it takes no traffic, and that is worth seeing on the row
               rather than only in the summary above. */
            row.weightBp === 0 && 'border-warn',
          )}
          value={configured}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (Number.isFinite(next)) props.onWeight(next);
          }}
        />
        <span className="text-ink-subtle">%</span>
      </div>

      <div className="flex flex-col gap-(--space-1) text-[length:var(--text-small)]">
        {/* Named through `thumbLabel`, not `aria-label`: Radix puts
            `role="slider"` on the thumb, so the root's label would name a
            wrapper nobody reaches. */}
        <Slider
          thumbLabel={`Weight for ${row.url}`}
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          step={SLIDER_STEP}
          disabled={!canEdit}
          value={[configured]}
          onValueChange={([next]) => {
            if (next !== undefined) props.onWeight(next);
          }}
        />
        {/*
          Always shown, not only when something is paused.
          The configured weight and the share of live traffic are two different
          numbers whenever the weights do not add to 100% — which, now that a
          typed weight stays typed, is most of the time while editing. Showing
          the live figure only in the paused case left the operator to work out
          the difference in their head for every other case.
        */}
        <span
          className={clsx('tabular-nums', row.enabled ? 'text-ink-muted' : 'font-medium text-warn')}
        >
          {row.enabled ? (
            <>
              <span className="font-medium text-ink">{formatPercent(props.effectivePct)}</span> of
              live traffic
            </>
          ) : (
            'Paused — 0% of live traffic'
          )}
        </span>
      </div>

      {canEdit && (
        <div className="flex items-center justify-end gap-(--space-1)">
          <IconButton
            label={row.locked ? 'Unlock this weight' : 'Lock this weight'}
            pressed={row.locked}
            onClick={props.onToggleLock}
          >
            {row.locked ? <LockIcon aria-hidden="true" /> : <LockOpenIcon aria-hidden="true" />}
          </IconButton>
          <IconButton
            label={row.enabled ? 'Pause this destination' : 'Resume this destination'}
            pressed={!row.enabled}
            onClick={props.onToggleEnabled}
            /* Paused is warn everywhere else in the console — the split bar, the
               callout, the status badge — so the pressed fill is warn here too
               rather than the accent, which means live traffic. */
            className={row.enabled ? undefined : 'bg-(--color-warn-quiet) text-warn'}
          >
            {row.enabled ? <PauseIcon aria-hidden="true" /> : <PlayIcon aria-hidden="true" />}
          </IconButton>
          {/* No move up/down here any more. Row order carried no meaning — the
              selector walks cumulative weights, so which row is third changes
              nothing about where traffic goes — and two more buttons on every
              row bought only the ability to rearrange a list nobody reads in
              order. The toolbar's sort control is how the list gets ordered
              now, by something that does mean something. */}
          <IconButton
            label="Remove destination"
            side="left"
            /* A trash can, not an X. An X on a row of controls reads as
               "dismiss this" — the same glyph that closes a dialog without
               consequence — and this button destroys a destination. */
            className="text-danger hover:bg-(--color-danger-quiet) hover:text-danger"
            onClick={props.onRemove}
          >
            <Trash2Icon aria-hidden="true" />
          </IconButton>
        </div>
      )}
    </div>
  );
}
